"use client";

import React, { useEffect, useState } from "react";
import { Truck, LockOpen, Cpu, Key, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const steps = [{
  id: 1,
  title: "Rapid Arrival",
  description: "Our mobile unit is dispatched immediately to your location in Norfolk or East Anglia. We track traffic in real-time to ensure the fastest possible response for your emergency.",
  icon: Truck
}, {
  id: 2,
  title: "Non-Destructive Entry",
  description: "Using specialized picking tools, we gain entry to your vehicle without causing any damage to the lock or door mechanism, preserving your car's integrity.",
  icon: LockOpen
}, {
  id: 3,
  title: "Digital Decoding",
  description: "We interface with your vehicle's onboard computer to decode the lock cylinder and extract the necessary key data, bypassing the need for dealership codes.",
  icon: Cpu
}, {
  id: 4,
  title: "Precision Cutting",
  description: "Your new key is cut on-site using high-precision CNC milling machines, then programmed to immobilizer systems for a perfect factory-grade replacement.",
  icon: Key
}];
function TimelineItem({
  step,
  index,
  isLast
}: {
  step: typeof steps[0];
  index: number;
  isLast: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3,
    once: true
  });
  const Icon = step.icon;
  return <div ref={ref as React.RefObject<HTMLDivElement>} className={cn("relative flex gap-8 md:gap-12 transition-opacity duration-1000", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")} data-index={index}>
      {}
      <div className="flex flex-col items-center">
        <div className={cn("relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-700 delay-300", isVisible ? "border-primary bg-primary/10 text-primary shadow-[0_0_15px_rgba(234,179,8,0.5)]" : "border-muted bg-background text-muted-foreground")}>
          <span className="text-lg font-bold font-mono">{step.id}</span>
        </div>
        {!isLast && <div className="relative h-full w-0.5 bg-muted/30 my-2 overflow-hidden">
            <div className={cn("absolute top-0 left-0 w-full bg-primary transition-all duration-1000 ease-out", isVisible ? "h-full" : "h-0")} />
          </div>}
      </div>

      {}
      <div className="pb-16 w-full">
        <Card className={cn("border-border/50 bg-card/50 backdrop-blur-sm transition-all duration-500 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5", isVisible ? "translate-x-0 opacity-100" : "translate-x-8 opacity-0")}>
          <CardHeader className="flex flex-row items-start gap-4 space-y-0 pb-2">
            <div className="p-2 rounded-lg bg-primary/10 text-primary">
              <Icon className="h-6 w-6" />
            </div>
            <div className="space-y-1">
              <CardTitle className="text-xl font-bold tracking-tight text-foreground">
                {step.title}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <CardDescription className="text-base text-muted-foreground leading-relaxed">
              {step.description}
            </CardDescription>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function TheReplacementProcess() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="the-replacement-process" className="relative w-full py-24 overflow-hidden bg-background">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-y-1/2" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl translate-y-1/2" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
      </div>

      <div className="container relative mx-auto px-4 md:px-6 max-w-5xl">
        <div ref={headerRef as React.RefObject<HTMLDivElement>} className={cn("text-center mb-16 space-y-4 transition-all duration-700", headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter text-foreground">
            The Replacement <span className="text-primary">Process</span>
          </h2>
          <p className="mx-auto max-w-[700px] text-muted-foreground md:text-lg">
            From emergency call to back on the road. Our streamlined 4-step protocol ensures precision, speed, and security.
          </p>
        </div>

        <div className="relative mx-auto max-w-3xl">
          {steps.map((step, index) => <TimelineItem key={step.id} step={step} index={index} isLast={index === steps.length - 1} />)}
        </div>

        <div className="mt-8 flex justify-center">
          <Button asChild size="lg" className="text-base font-semibold shadow-lg shadow-primary/20">
            <Link to="/contact">
              Start Your Replacement <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}